// Alcance de la regla por dimensión: tipo, gama, SKU y tienda. Una lista vacía es "todos".
// Mockup: portal-admin-vl-comisiones.html
import { useState } from 'react';
import { ProductCategoryEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductCategoryEnum';
import { ProductTierEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductTierEnum';
import {
    ScopeChip,
    ScopeChipGrid,
    ScopeChipGroup,
    ScopeChipSearch,
} from '@/shared/patterns/ScopeChips';
import type { CommissionDirectory } from '../../api/useCommissionDirectory';
import type { RuleFormValues } from '../../lib/ruleForm';
import { CATEGORY_LABEL, TIER_LABEL } from '../../lib/ruleText';
import { RULE_HELP } from '../../constants/helpText';

type ScopeValues = Pick<RuleFormValues, 'tipos' | 'gamas' | 'skus' | 'tiendas'>;

/** Chips visibles en las listas con buscador; los elegidos siempre se ven. */
const SHOWN = 40;

function toggle<T>(list: readonly T[], value: T): T[] {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function matches(text: string, query: string): boolean {
    const q = query.trim().toLowerCase();
    return q === '' || text.toLowerCase().includes(q);
}

interface RuleScopePickerProps {
    values: ScopeValues;
    onChange: (patch: Partial<ScopeValues>) => void;
    directory: CommissionDirectory;
}

export function RuleScopePicker({ values, onChange, directory }: RuleScopePickerProps) {
    const [skuQuery, setSkuQuery] = useState('');
    const [storeQuery, setStoreQuery] = useState('');

    const skuMatches = directory.products
        .filter((p) => matches(`${p.sku} ${p.brand ?? ''} ${p.model ?? ''}`, skuQuery))
        .map((p) => p.sku);
    const skus = [...new Set([...values.skus, ...skuMatches])].slice(
        0,
        Math.max(SHOWN, values.skus.length),
    );
    const storeMatches = directory.stores
        .filter((s) => matches(`${s.name} ${s.code}`, storeQuery))
        .map((s) => s.storeId);
    const stores = [...new Set([...values.tiendas, ...storeMatches])].slice(
        0,
        Math.max(SHOWN, values.tiendas.length),
    );

    const skuLabel = (sku: string) => {
        const product = directory.product(sku);
        const name = product ? `${product.brand ?? ''} ${product.model ?? ''}`.trim() : '';
        return name === '' ? sku : name;
    };

    return (
        <ScopeChipGrid>
            <ScopeChipGroup title="Tipo de producto" help={RULE_HELP.scopeType}>
                <ScopeChip
                    dim="segment"
                    label="Todos"
                    selected={values.tipos.length === 0}
                    onToggle={() => {
                        onChange({ tipos: [] });
                    }}
                />
                {Object.values(ProductCategoryEnum).map((tipo) => (
                    <ScopeChip
                        key={tipo}
                        dim="segment"
                        label={CATEGORY_LABEL[tipo]}
                        selected={values.tipos.includes(tipo)}
                        onToggle={() => {
                            onChange({ tipos: toggle(values.tipos, tipo) });
                        }}
                    />
                ))}
            </ScopeChipGroup>

            <ScopeChipGroup title="Gama" help={RULE_HELP.scopeTier}>
                <ScopeChip
                    dim="tier"
                    label="Todas"
                    selected={values.gamas.length === 0}
                    onToggle={() => {
                        onChange({ gamas: [] });
                    }}
                />
                {Object.values(ProductTierEnum).map((gama) => (
                    <ScopeChip
                        key={gama}
                        dim="tier"
                        label={TIER_LABEL[gama]}
                        selected={values.gamas.includes(gama)}
                        onToggle={() => {
                            onChange({ gamas: toggle(values.gamas, gama) });
                        }}
                    />
                ))}
            </ScopeChipGroup>

            <ScopeChipGroup
                title="Producto (SKU)"
                help={RULE_HELP.scopeSku}
                search={
                    <ScopeChipSearch
                        value={skuQuery}
                        placeholder="Buscar SKU o marca…"
                        label="Buscar SKU"
                        onChange={setSkuQuery}
                    />
                }
                footer={
                    skuMatches.length > SHOWN ? (
                        <span className="text-[10px] text-muted-foreground">
                            Busca para ver más productos.
                        </span>
                    ) : undefined
                }
            >
                <ScopeChip
                    dim="product"
                    label="Todos"
                    selected={values.skus.length === 0}
                    onToggle={() => {
                        onChange({ skus: [] });
                    }}
                />
                {skus.map((sku) => (
                    <ScopeChip
                        key={sku}
                        dim="product"
                        label={skuLabel(sku)}
                        detail={sku}
                        selected={values.skus.includes(sku)}
                        onToggle={() => {
                            onChange({ skus: toggle(values.skus, sku) });
                        }}
                    />
                ))}
            </ScopeChipGroup>

            <ScopeChipGroup
                title="Tienda"
                help={RULE_HELP.scopeStore}
                search={
                    <ScopeChipSearch
                        value={storeQuery}
                        placeholder="Buscar tienda…"
                        label="Buscar tienda"
                        onChange={setStoreQuery}
                    />
                }
            >
                <ScopeChip
                    dim="store"
                    label="Todas"
                    selected={values.tiendas.length === 0}
                    onToggle={() => {
                        onChange({ tiendas: [] });
                    }}
                />
                {stores.map((storeId) => (
                    <ScopeChip
                        key={storeId}
                        dim="store"
                        label={directory.storeName(storeId)}
                        detail={directory.storeZone(storeId) ?? undefined}
                        selected={values.tiendas.includes(storeId)}
                        onToggle={() => {
                            onChange({ tiendas: toggle(values.tiendas, storeId) });
                        }}
                    />
                ))}
            </ScopeChipGroup>
        </ScopeChipGrid>
    );
}
